import React, {
  InputHTMLAttributes,
  ReactNode,
  forwardRef,
  useRef,
  useEffect,
  ChangeEvent,
  FocusEvent,
  useImperativeHandle,
  Ref,
} from 'react';
import classNames from 'classnames';
import datePicker from '@uswds/uswds/packages/usa-date-picker/src';
import FormControlWrapper from '../FormControlWrapper';
import controlHintIds from '../controlHintIds';
import Input from '../Input';
import { ErrorClassName } from '../InputVariant';
import DateInputMask from './InputMask/DateInputMask';

export interface DatePickerProps extends InputHTMLAttributes<HTMLInputElement> {
  id: string;
  label: string;
  className?: string;
  inputClassName?: string;
  errorMessage?: string;
  hint?: string;
  labelHint?: ReactNode;
  note?: ReactNode;
  requiredLabel?: ReactNode;
  /**
   * The date should be in the format YYYY-MM-DD.
   */
  defaultValue?: string;
  /**
   * The date picker will not allow a date selection before this date.
   * The date should be in the format YYYY-MM-DD.
   * Typing in an earlier date will cause native form validation error.
   * A default min date or 0000-01-01 is used as a default.
   */
  minDate?: string;
  maxDate?: string;
  rangeDate?: string;
  methodsRef?: Ref<DatePickerMethods>;
  mask?: string;
}

export interface DatePickerMethods {
  /**
   * Return result of USWDS DatePicker validation
   */
  isDateInputInvalid: () => boolean;
  /**
   * Return USWDS validation message applied to external input
   */
  getValidationMessage: () => string;
}

const externalInputClassName = '.usa-date-picker__external-input';
function useDatePicker(
  errorId: string | undefined,
  errorMessage: string | undefined,
  ariaDescribedBy: string | undefined,
  ref: React.ForwardedRef<HTMLInputElement | null>,
  methodsRef: React.Ref<DatePickerMethods> | undefined,
  onChange: React.ChangeEventHandler<HTMLInputElement> | undefined,
  onBlur: React.FocusEventHandler<HTMLInputElement> | undefined,
  onInput: React.ChangeEventHandler<HTMLInputElement> | undefined,
  disabled: boolean | undefined,
  mask: string | undefined,
) {
  const datePickerRef = useRef<HTMLDivElement>(null);
  const datePickerInputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    const currentDatePickerRef = datePickerRef.current;
    datePicker.on(currentDatePickerRef);

    return () => {
      datePicker.off(currentDatePickerRef);
    };
  }, []);
  useEffect(() => {
    const currentDatePickerRef = datePickerRef.current;
    const externalInput = currentDatePickerRef?.querySelector<HTMLInputElement>(externalInputClassName) || null;
    if (ref) {
      if (typeof ref === 'function') {
        ref(externalInput);
      } else {
        // eslint-disable-next-line no-param-reassign
        ref.current = externalInput;
      }
    }
  }, [ref]);
  useEffect(() => {
    // Manually add and remove error class from external input
    const externalInputRef = datePickerRef.current?.querySelector<HTMLInputElement>(externalInputClassName);
    if (errorId && errorMessage) {
      externalInputRef?.classList.add(ErrorClassName);
    } else {
      externalInputRef?.classList.remove(ErrorClassName);
    }
    if (ariaDescribedBy) {
      externalInputRef?.setAttribute('aria-describedby', ariaDescribedBy);
    }
  }, [errorMessage, errorId, ariaDescribedBy]);

  useImperativeHandle(methodsRef, () => ({
    isDateInputInvalid: (): boolean => {
      if (datePickerInputRef.current) {
        return datePicker.isDateInputInvalid(datePickerInputRef.current);
      }
      return false;
    },
    getValidationMessage: (): string => {
      const externalInputRef = datePickerRef.current?.querySelector<HTMLInputElement>(externalInputClassName);
      return externalInputRef?.validationMessage || '';
    },
  }));

  useEffect(() => {
    if (!datePickerInputRef.current) {
      return;
    }

    if (disabled) {
      datePicker.disable(datePickerInputRef.current);
    } else {
      datePicker.enable(datePickerInputRef.current);
    }
  }, [disabled]);
  useEffect(() => {
    const changeHandler = (event: Event) => {
      if (onChange) {
        onChange((event as unknown) as ChangeEvent<HTMLInputElement>);
      }
    };
    const externalInputRef = datePickerRef.current?.querySelector<HTMLInputElement>(externalInputClassName);
    externalInputRef?.addEventListener('change', changeHandler);

    return () => {
      externalInputRef?.removeEventListener('change', changeHandler);
    };
  }, [ref, onChange]);

  useEffect(() => {
    const externalInputRef = datePickerRef.current?.querySelector<HTMLInputElement>(externalInputClassName);
    const onInputHandler = (event: Event) => {
      if (!externalInputRef || !mask) {
        return;
      }

      const e = (event as unknown) as ChangeEvent<HTMLInputElement>;

      const rawValue = e.currentTarget.value;

      // Use the utility function to handle the input and determine the cursor position
      const { maskedDate, cursorPosition } = DateInputMask(rawValue, mask);
      externalInputRef.value = maskedDate;

      // Set cursor position after the last valid character
      setTimeout(() => {
        externalInputRef.setSelectionRange(cursorPosition, cursorPosition);
      }, 0);
    };
    externalInputRef?.addEventListener('input', onInputHandler);

    return () => {
      externalInputRef?.removeEventListener('input', onInputHandler);
    };
  }, [ref, onInput, mask]);
  useEffect(() => {
    const focusoutHandler = (event: Event) => {
      if (onBlur) {
        onBlur((event as unknown) as FocusEvent<HTMLInputElement>);
      }
    };
    // External input is created by uswds so manually attaching focusout handler
    const externalInputRef = datePickerRef.current?.querySelector<HTMLInputElement>(externalInputClassName);
    externalInputRef?.addEventListener('focusout', focusoutHandler);

    return () => {
      externalInputRef?.removeEventListener('focusout', focusoutHandler);
    };
  }, [onBlur]);
  return { datePickerRef, datePickerInputRef };
}
/**
 * The DatePicker based on [The USWDS Date Picker component](https://designsystem.digital.gov/components/date-picker/)
 *
 * The DatePicker is implemented as two text input fields:
 * - **internal input**: Rendered by the component and is visually hidden by USWDS code.
 * - **external input**: And a new text input created by USWDS code that the user can see and type in. This input is assigned the ref.
 *
 * The internal input receives dates in the format `YYYY-DD-MM` and the external input displays dates in the format `MM/DD/YYYY`.
 *
 * note: Configured `requiredLabel` text is appended to the labelhint if required attribute is true.
 */
const DatePicker = forwardRef<HTMLInputElement, DatePickerProps>(
  (
    {
      id,
      name,
      className,
      inputClassName,
      label,
      errorMessage,
      hint = 'mm/dd/yyyy',
      labelHint,
      note,
      onChange,
      onBlur,
      onInput,
      value,
      type = 'text',
      maxLength,
      'aria-describedby': ariaDescribedBy,
      required,
      requiredLabel,
      defaultValue,
      minDate,
      maxDate,
      rangeDate,
      methodsRef,
      disabled,
      mask,
      ...rest
    }: DatePickerProps,
    ref,
  ): JSX.Element => {
    const { labelHintId, hintId, errorId, describedBy } = controlHintIds(
      id,
      labelHint,
      hint,
      errorMessage,
      undefined,
      ariaDescribedBy,
    );
    const { datePickerRef, datePickerInputRef } = useDatePicker(
      errorId,
      errorMessage,
      describedBy,
      ref,
      methodsRef,
      onChange,
      onBlur,
      onInput,
      disabled,
      mask,
    );
    return (
      <FormControlWrapper
        id={id}
        label={label}
        labelHint={labelHint}
        labelHintId={labelHintId}
        hint={hint}
        hintId={hintId}
        note={note}
        errorId={errorId}
        errorMessage={errorMessage}
        required={required}
        requiredLabel={requiredLabel}
        formGroupRef={datePickerRef}
      >
        <div
          // Including .usa-date-picker--initialized as a workaround to prevent rerenders breaking the uswds code
          className={classNames('usa-date-picker', 'usa-date-picker--initialized', className)}
          data-default-value={defaultValue}
          data-min-date={minDate}
          data-max-date={maxDate}
          data-range-date={rangeDate}
        >
          <Input
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...rest}
            aria-describedby={describedBy}
            type={type}
            id={id}
            name={name}
            className={classNames('usa-date-picker__input', inputClassName)}
            ref={datePickerInputRef}
            value={value}
            aria-required={required}
            required={required}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            // Property is valid, but probably not worth creating a new component just to avoid a ts/lint error.
            mask={mask}
          />
        </div>
      </FormControlWrapper>
    );
  },
);

export default DatePicker;
